/**
 * Shared validation utilities for The Mat Monster app
 * Provides consistent validation patterns across all forms
 */

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Email validation using the same pattern as ContactForm
 * Validates email format with comprehensive regex pattern
 */
export const validateEmail = (email: string): ValidationResult => {
  if (!email || email.trim() === '') {
    return {
      isValid: false,
      errorMessage: 'Email address is required'
    };
  }

  const trimmedEmail = email.trim();
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailPattern.test(trimmedEmail)) {
    return {
      isValid: false,
      errorMessage: 'Please enter a valid email address'
    };
  }

  return { isValid: true };
};

/**
 * Password validation with configurable minimum length
 * Default minimum is 8 characters to match profile requirements
 */
export const validatePassword = (password: string, minLength: number = 8): ValidationResult => {
  if (!password || password.trim() === '') {
    return {
      isValid: false,
      errorMessage: 'Password is required'
    };
  }

  if (password.length < minLength) {
    return {
      isValid: false,
      errorMessage: `Password must be at least ${minLength} characters`
    };
  }

  return { isValid: true };
};

/**
 * Enhanced password validation with strength requirements
 * Provides detailed feedback for password improvement
 */
export const validatePasswordStrength = (password: string, minLength: number = 8): {
  isValid: boolean;
  isStrong: boolean;
  errorMessage?: string;
  suggestions: string[];
} => {
  const suggestions: string[] = [];

  if (!password || password.trim() === '') {
    return {
      isValid: false,
      isStrong: false,
      errorMessage: 'Password is required',
      suggestions: []
    };
  }

  // Basic length requirement
  if (password.length < minLength) {
    return {
      isValid: false,
      isStrong: false,
      errorMessage: `Password must be at least ${minLength} characters`,
      suggestions: [`Use at least ${minLength} characters`]
    };
  }

  // Strength checks
  if (!/[A-Z]/.test(password)) {
    suggestions.push('Add an uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    suggestions.push('Add a lowercase letter');
  }
  if (!/\d/.test(password)) {
    suggestions.push('Add a number');
  }
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    suggestions.push('Add a special character');
  }

  const isStrong = suggestions.length === 0;

  return {
    isValid: true,
    isStrong,
    suggestions
  };
};

/**
 * Full name validation
 * Ensures name is provided and meets minimum requirements
 */
export const validateFullName = (fullName: string): ValidationResult => {
  if (!fullName || fullName.trim() === '') {
    return {
      isValid: false,
      errorMessage: 'Full name is required'
    };
  }

  const trimmedName = fullName.trim();
  if (trimmedName.length < 2) {
    return {
      isValid: false,
      errorMessage: 'Full name must be at least 2 characters'
    };
  }

  return { isValid: true };
};

/**
 * Real-time email validation for form inputs
 * Returns validation state for immediate user feedback
 */
export const getEmailValidationState = (email: string): {
  isValid: boolean;
  isEmpty: boolean;
  errorMessage?: string;
} => {
  const isEmpty = !email || email.trim() === '';
  
  if (isEmpty) {
    return { isValid: false, isEmpty: true };
  }

  const validation = validateEmail(email);
  return {
    isValid: validation.isValid,
    isEmpty: false,
    errorMessage: validation.errorMessage
  };
};

/**
 * Comprehensive form validation for signup
 * Validates all required fields and returns detailed results
 */
export const validateSignupForm = (data: {
  email: string;
  password: string;
  fullName?: string;
}): {
  isValid: boolean;
  errors: {
    email?: string;
    password?: string;
    fullName?: string;
  };
} => {
  const errors: { email?: string; password?: string; fullName?: string } = {};

  // Validate email
  const emailValidation = validateEmail(data.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.errorMessage;
  }

  // Validate password (8 characters minimum to match profile requirements)
  const passwordValidation = validatePassword(data.password, 8);
  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.errorMessage;
  }

  // Validate full name if provided
  if (data.fullName !== undefined) {
    const fullNameValidation = validateFullName(data.fullName);
    if (!fullNameValidation.isValid) {
      errors.fullName = fullNameValidation.errorMessage;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
